const padDatePart = (value) => String(value).padStart(2, '0');
const toIsoDate = (date) => `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`;
const parseIsoDate = (value) => {
  const [year, month, day] = String(value || '').split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day, 12);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) return null;
  return date;
};
const addDays = (date, days) => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  next.setDate(next.getDate() + Number(days || 0));
  return next;
};
const firstOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1, 12);
const currentTimeInZone = (timeZone) => {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date());
    const values = Object.fromEntries(
      parts
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value])
    );
    const year = Number(values.year);
    const month = Number(values.month);
    const day = Number(values.day);
    const hour = Number(values.hour);
    if (!year || !month || !day || !Number.isFinite(hour)) return null;
    return { date: new Date(year, month - 1, day, 12), hour };
  } catch (_) {
    return null;
  }
};

if (!customElements.get('product-delivery-calendar')) {
  customElements.define(
    'product-delivery-calendar',
    class ProductDeliveryCalendar extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialised === 'true') {
          this.form?.addEventListener('submit', this.handleSubmit, true);
          return;
        }

        this.dataset.initialised = 'true';
        this.input = this.querySelector('[data-delivery-date-input]');
        this.ui = this.querySelector('[data-calendar-ui]');
        this.grid = this.querySelector('[data-calendar-grid]');
        this.monthLabel = this.querySelector('[data-calendar-month-label]');
        this.selection = this.querySelector('[data-calendar-selection]');
        this.summaryValue = this.querySelector('[data-calendar-summary-value]');
        this.disclosure = this.querySelector('[data-delivery-disclosure]');
        this.previousButton = this.querySelector('[data-calendar-prev]');
        this.nextButton = this.querySelector('[data-calendar-next]');
        this.form = this.closest('form');

        if (!this.input || !this.ui || !this.grid) return;

        this.allowedWeekdays = new Set(
          String(this.dataset.allowedWeekdays || '')
            .split(',')
            .map((value) => value.trim())
            .filter((value) => value !== '')
            .map(Number)
            .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
        );

        this.blockedDates = new Set(
          String(this.dataset.blockedDates || '')
            .split(/[\s,;]+/)
            .map((value) => value.trim())
            .filter((value) => /^\d{4}-\d{2}-\d{2}$/.test(value))
        );

        this.storeTimeZone = this.dataset.storeTimeZone || 'Europe/London';
        this.fallbackStoreDate = parseIsoDate(this.dataset.storeToday);
        this.fallbackStoreHour = Number(this.dataset.storeHour || 0);
        this.noticeDays = Math.max(0, Number(this.dataset.minimumNoticeDays || 0));
        this.cutoffHour = Number(this.dataset.sameDayCutoffHour || 24);
        this.afterCutoffMinimumNoticeDays = Math.max(
          1,
          Number(this.dataset.afterCutoffMinimumNoticeDays || 2)
        );
        this.monthsAhead = Math.max(1, Number(this.dataset.monthsAhead || 4));

        this.refreshBounds();

        const selected = parseIsoDate(this.input.value);
        if (selected && !this.isSelectable(selected)) this.input.value = '';
        this.currentMonth = firstOfMonth(parseIsoDate(this.input.value) || this.minimumDate);

        this.previousButton?.addEventListener('click', () => this.changeMonth(-1));
        this.nextButton?.addEventListener('click', () => this.changeMonth(1));
        this.grid.addEventListener('click', this.handleDateClick);
        this.form?.addEventListener('submit', this.handleSubmit, true);

        this.input.addEventListener('change', () => {
          this.refreshBounds();
          const date = parseIsoDate(this.input.value);
          if (this.input.value && !this.isSelectable(date)) {
            this.input.value = '';
            this.showValidationError('That delivery date is not available. Choose one of the dates shown.');
          } else {
            this.input.setCustomValidity('');
            this.selection?.removeAttribute('role');
            if (date) this.currentMonth = firstOfMonth(date);
          }
          this.render();
        });

        this.dataset.enhanced = 'true';
        this.ui.hidden = false;
        this.render();
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.handleSubmit, true);
      }

      getStoreNow() {
        const zonedNow = currentTimeInZone(this.storeTimeZone);
        const date = zonedNow?.date || this.fallbackStoreDate || new Date();
        const hour = Number.isFinite(zonedNow?.hour) ? zonedNow.hour : this.fallbackStoreHour;
        return { date, hour };
      }

      refreshBounds() {
        const storeNow = this.getStoreNow();
        this.minimumDate = addDays(storeNow.date, this.noticeDays);

        // Short-notice cakes: after the cut-off, both today and tomorrow are closed.
        // Recalculate this on every interaction/submit so a page opened before noon
        // cannot be used to place a next-day order after noon.
        if (this.noticeDays === 0 && storeNow.hour >= this.cutoffHour) {
          this.minimumDate = addDays(storeNow.date, this.afterCutoffMinimumNoticeDays);
        }

        this.maximumDate = new Date(
          storeNow.date.getFullYear(),
          storeNow.date.getMonth() + this.monthsAhead + 1,
          0,
          12
        );

        this.input.min = toIsoDate(this.minimumDate);
        this.input.max = toIsoDate(this.maximumDate);
      }

      showValidationError(message) {
        this.disclosure?.setAttribute('open', '');
        this.input.setCustomValidity(message);
        if (this.selection) {
          this.selection.textContent = message;
          this.selection.setAttribute('role', 'alert');
        }
      }

      handleSubmit = (event) => {
        if (!this.input) return;

        this.refreshBounds();
        const selected = parseIsoDate(this.input.value);
        const missingRequired = this.input.required && !this.input.value;
        const invalidSelected = Boolean(this.input.value) && !this.isSelectable(selected);

        if (!missingRequired && !invalidSelected) {
          this.input.setCustomValidity('');
          this.selection?.removeAttribute('role');
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        const message = invalidSelected
          ? 'That delivery date is no longer available. Choose one of the dates shown.'
          : 'Choose a delivery date before adding this cake.';

        if (invalidSelected) this.input.value = '';
        this.showValidationError(message);
        this.render();

        requestAnimationFrame(() => {
          this.grid.querySelector('[data-calendar-date]:not(:disabled)')?.focus();
        });
      };

      isSelectable(date) {
        if (!date) return false;
        const iso = toIsoDate(date);
        return (
          date >= this.minimumDate &&
          date <= this.maximumDate &&
          this.allowedWeekdays.has(date.getDay()) &&
          !this.blockedDates.has(iso)
        );
      }

      changeMonth(direction) {
        this.refreshBounds();
        const next = new Date(
          this.currentMonth.getFullYear(),
          this.currentMonth.getMonth() + direction,
          1,
          12
        );
        const minimumMonth = firstOfMonth(this.minimumDate);
        const maximumMonth = firstOfMonth(this.maximumDate);
        if (next < minimumMonth || next > maximumMonth) return;
        this.currentMonth = next;
        this.render();
      }

      handleDateClick = (event) => {
        const button = event.target.closest('[data-calendar-date]');
        if (!button || button.disabled) return;

        this.refreshBounds();
        const date = parseIsoDate(button.dataset.calendarDate);

        if (!this.isSelectable(date)) {
          this.render();
          this.showValidationError('That delivery date is no longer available. Choose one of the dates shown.');
          return;
        }

        this.input.value = button.dataset.calendarDate;
        this.input.setCustomValidity('');
        this.selection?.removeAttribute('role');
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };

      render() {
        this.refreshBounds();

        const minimumMonth = firstOfMonth(this.minimumDate);
        const maximumMonth = firstOfMonth(this.maximumDate);

        if (!this.currentMonth || this.currentMonth < minimumMonth) {
          this.currentMonth = minimumMonth;
        } else if (this.currentMonth > maximumMonth) {
          this.currentMonth = maximumMonth;
        }

        const selected = parseIsoDate(this.input.value);
        if (selected && !this.isSelectable(selected)) {
          this.input.value = '';
        }

        const locale = document.documentElement.lang || 'en-GB';
        if (this.monthLabel) {
          this.monthLabel.textContent = new Intl.DateTimeFormat(locale, {
            month: 'long',
            year: 'numeric'
          }).format(this.currentMonth);
        }

        const firstDay = new Date(
          this.currentMonth.getFullYear(),
          this.currentMonth.getMonth(),
          1,
          12
        );
        const lastDay = new Date(
          this.currentMonth.getFullYear(),
          this.currentMonth.getMonth() + 1,
          0,
          12
        );
        const mondayOffset = (firstDay.getDay() + 6) % 7;
        const fragment = document.createDocumentFragment();

        for (let index = 0; index < mondayOffset; index += 1) {
          const spacer = document.createElement('span');
          spacer.className = 'delivery-calendar__spacer';
          spacer.setAttribute('aria-hidden', 'true');
          fragment.appendChild(spacer);
        }

        const selectedIso = this.input.value;
        for (let day = 1; day <= lastDay.getDate(); day += 1) {
          const date = new Date(
            this.currentMonth.getFullYear(),
            this.currentMonth.getMonth(),
            day,
            12
          );
          const iso = toIsoDate(date);
          const button = document.createElement('button');

          button.type = 'button';
          button.className = 'delivery-calendar__day';
          button.textContent = String(day);
          button.dataset.calendarDate = iso;
          button.disabled = !this.isSelectable(date);
          button.setAttribute(
            'aria-label',
            new Intl.DateTimeFormat(locale, {
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            }).format(date)
          );

          if (iso === selectedIso) {
            button.classList.add('is-selected');
            button.setAttribute('aria-pressed', 'true');
          }

          fragment.appendChild(button);
        }

        this.grid.replaceChildren(fragment);

        if (this.previousButton) this.previousButton.disabled = this.currentMonth <= minimumMonth;
        if (this.nextButton) this.nextButton.disabled = this.currentMonth >= maximumMonth;

        const activeSelection = parseIsoDate(this.input.value);
        if (activeSelection) {
          const summaryDate = new Intl.DateTimeFormat(locale, {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
          }).format(activeSelection);
          const statusDate = new Intl.DateTimeFormat(locale, {
            weekday: 'long',
            day: 'numeric',
            month: 'long'
          }).format(activeSelection);

          if (this.summaryValue) this.summaryValue.textContent = summaryDate;
          if (this.selection && this.selection.getAttribute('role') !== 'alert') {
            this.selection.textContent = `Selected: ${statusDate}`;
          }
        } else {
          if (this.summaryValue) this.summaryValue.textContent = 'Choose a date';
          if (this.selection && this.selection.getAttribute('role') !== 'alert') {
            this.selection.textContent = 'Choose a delivery date to continue.';
          }
        }
      }
    }
  );
}
